export const normalizePlayerName = (name) =>
  (name || '').trim().toLowerCase();

const looksLikeEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

export const buildRoster = (players = [], members = []) => {
  const byName = new Map();

  players.forEach((player) => {
    if (player?.migratedToMemberId) return;
    if (looksLikeEmail(player?.name)) return;
    const key = normalizePlayerName(player?.name);
    if (!key) return;

    byName.set(key, {
      kind: 'legacy',
      id: player.id,
      key: `legacy:${player.id}`,
      name: player.name || '(bez imena)',
      email: player.email || null,
      role: 'player',
      claimed: false,
      goals: player.goals || 0,
      assists: player.assists || 0,
      matchesPlayed: player.matchesPlayed || 0,
      stats: player.stats || {},
      raw: player,
    });
  });

  members.forEach((member) => {
    if (member?.removed) return;
    if (member?.role === 'owner') return;
    const key = normalizePlayerName(member?.displayName || member?.email);
    if (!key) return;

    const legacy = byName.get(key);
    byName.set(key, {
      kind: 'member',
      id: member.id,
      key: `member:${member.id}`,
      name: member.displayName || member.email || '(bez imena)',
      email: member.email || null,
      role: member.role || 'player',
      claimed: !!member.claimed,
      goals: member.goals || legacy?.goals || 0,
      assists: member.assists || legacy?.assists || 0,
      matchesPlayed: member.matchesPlayed || legacy?.matchesPlayed || 0,
      stats:
        member.stats && Object.keys(member.stats).length > 0
          ? member.stats
          : legacy?.stats || {},
      raw: member,
      legacyId: legacy?.kind === 'legacy' ? legacy.id : null,
      legacyMerged: legacy?.kind === 'legacy',
    });
  });

  return Array.from(byName.values()).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );
};

export const calculatePlayerStatsFromMatches = (player, matches = []) => {
  const name = player?.name || '';
  let wins = 0;
  let draws = 0;
  let losses = 0;
  let goalsFor = 0;
  let goalsAgainst = 0;
  let matchesPlayed = 0;
  let goals = 0;
  let assists = 0;

  matches.forEach((match) => {
    const isInTeamA = match.teamA?.includes(name);
    const isInTeamB = match.teamB?.includes(name);
    const statLine = match.stats?.[name];

    if (statLine) {
      goals += statLine.goals || 0;
      assists += statLine.assists || 0;
    }

    if (isInTeamA || isInTeamB) {
      matchesPlayed += 1;
      const isTeamA = isInTeamA;
      const teamGoals = Number(isTeamA ? match.scoreA : match.scoreB) || 0;
      const opponentGoals = Number(isTeamA ? match.scoreB : match.scoreA) || 0;

      goalsFor += teamGoals;
      goalsAgainst += opponentGoals;

      if (teamGoals > opponentGoals) wins += 1;
      else if (teamGoals < opponentGoals) losses += 1;
      else draws += 1;
    }
  });

  const successRate = matchesPlayed > 0
    ? `${((wins / matchesPlayed) * 100).toFixed(1)}%`
    : '0%';
  const points = wins * 3 + draws * 2 + losses;
  const successPoints = ((parseFloat(successRate) / 100) * points).toFixed(1);
  const goalDifference = goalsFor - goalsAgainst;
  const mvpPoints = goals + assists * 0.5 + wins * 3 + goalDifference * 0.5;

  return {
    ...player,
    wins,
    draws,
    losses,
    matchesPlayed,
    goals,
    assists,
    points,
    goalsFor,
    goalsAgainst,
    goalDifference,
    successRate,
    successPoints,
    mvpPoints: mvpPoints.toFixed(1),
  };
};

const getMvpFromMatch = (match) => {
  const allNames = [...(match.teamA || []), ...(match.teamB || [])];
  const withStats = allNames
    .map((name) => ({
      name,
      goals: match.stats?.[name]?.goals || 0,
      assists: match.stats?.[name]?.assists || 0,
    }))
    .filter((p) => p.goals > 0 || p.assists > 0);

  if (withStats.length === 0) return null;

  return withStats.sort((a, b) => {
    const aTotal = a.goals + a.assists;
    const bTotal = b.goals + b.assists;
    if (bTotal !== aTotal) return bTotal - aTotal;
    if (b.goals !== a.goals) return b.goals - a.goals;
    return a.name.localeCompare(b.name);
  })[0].name;
};

export const calculateRosterStats = (roster = [], matches = []) => {
  const mvpCounts = new Map();
  matches.forEach((match) => {
    const mvp = getMvpFromMatch(match);
    if (mvp) mvpCounts.set(mvp, (mvpCounts.get(mvp) || 0) + 1);
  });

  return roster.map((player) => ({
    ...calculatePlayerStatsFromMatches(player, matches),
    mvpCount: mvpCounts.get(player.name) || 0,
  }));
};
